import React from "react";
import { DropDownOptionBase } from "../../staff/DropDownMultiple";
import { useStaffMode } from "../../../hooks/StaffModeProvider";

/** Stable id for the synthetic "select all" option rendered by {@link FilterOptionsGroup}. */
export const ALL_FILTER_OPTIONS_ID = "__all_filter_options__";

interface FilterOptionsGroupProps {
  title?: string;
  options: DropDownOptionBase[];
  value: DropDownOptionBase[];
  onChange: (value: DropDownOptionBase[]) => void;
  allOptionLabel?: string;
  disabled?: boolean;
}

/**
 * Filter widget that always shows every option (plus a synthetic "All …" option) as a row of
 * radio buttons, instead of collapsing them behind a dropdown. Functionally equivalent to the
 * single-select `"select"` filter type — selecting an option (or "All") drives the same
 * `onHandleFilters` call in `FiltersPanel`.
 */
export default function FilterOptionsGroup({
  title,
  options,
  value,
  onChange,
  allOptionLabel,
  disabled = false,
}: FilterOptionsGroupProps): React.ReactElement {
  const { isStaffMode } = useStaffMode();
  const accentClass = isStaffMode
    ? "text-secondary accent-secondary"
    : "text-primary accent-primary";

  const allOption: DropDownOptionBase = {
    id: ALL_FILTER_OPTIONS_ID,
    name: allOptionLabel ?? `All ${title ?? ""}`.trim(),
  };
  const allOptions: DropDownOptionBase[] = [allOption, ...options];

  const selectedId =
    value?.[0]?.id != null ? String(value[0].id) : ALL_FILTER_OPTIONS_ID;

  const groupName = title ? `filter-options-${title}` : undefined;

  return (
    <div className="flex flex-col gap-2">
      {title && <div className="font-semibold text-gray-font">{title}</div>}

      <div
        role="radiogroup"
        aria-label={title}
        className="flex flex-row flex-wrap items-center gap-x-6 gap-y-2"
      >
        {allOptions.map((option, index) => {
          const optionId = String(option.id ?? index);
          const isAll = optionId === ALL_FILTER_OPTIONS_ID;
          const selected = optionId === selectedId;

          return (
            <label
              key={optionId}
              className={`group inline-flex max-w-full cursor-pointer items-center gap-2 ${
                disabled ? "cursor-not-allowed" : ""
              }`}
            >
              <input
                type="radio"
                name={groupName}
                checked={selected}
                disabled={disabled}
                onChange={() => onChange(isAll ? [] : [option])}
                className={`h-4 w-4 shrink-0 cursor-pointer ${accentClass} disabled:cursor-not-allowed disabled:opacity-50`}
              />
              <span
                className={`select-none text-sm ${
                  disabled ? "text-gray-400" : "text-gray-font"
                }`}
              >
                {option.name}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
